<script lang="ts" setup>
import {onMounted, onUnmounted, ref} from 'vue';
import {useKeyboard} from '@/composables/useKeyboard';
import Keyboard from 'simple-keyboard';
import 'simple-keyboard/build/css/index.css';

const emit = defineEmits(['keyPress']);
const {hideKeyboard, targetInput, onKeyboardInteractionStart} = useKeyboard();

const keyboard = ref<Keyboard | null>(null);
const keyboardContainer = ref<HTMLElement | null>(null);
const currentLayout = ref('default');

// Empêcher le clavier de voler le focus à l'input
const preventFocusLoss = (event: MouseEvent | TouchEvent) => {
  event.preventDefault();
  // Sauvegarder la position du curseur immédiatement
  onKeyboardInteractionStart();
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  // Vérifier si le clic est sur le clavier
  if (keyboardContainer.value?.contains(target)) {
    return;
  }

  // Vérifier si le clic est sur l'input actif
  if (targetInput.value && targetInput.value === target) {
    return;
  }

  // Vérifier si le clic est sur le bouton flottant du clavier
  if (target.closest('.keyboard-toggle-button')) {
    return;
  }

  // Sinon, cacher le clavier
  hideKeyboard();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  // Empêcher la perte de focus quand on clique/touche le clavier
  keyboardContainer.value?.addEventListener('mousedown', preventFocusLoss);
  keyboardContainer.value?.addEventListener('touchstart', preventFocusLoss, { passive: false });
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  keyboardContainer.value?.removeEventListener('mousedown', preventFocusLoss);
  keyboardContainer.value?.removeEventListener('touchstart', preventFocusLoss);
});

const handleLayoutChange = (layout: string) => {
  currentLayout.value = layout;
  keyboard.value?.setOptions({
    layoutName: layout,
  });
};

onMounted(() => {
  keyboard.value = new Keyboard('.simple-keyboard', {
    layoutName: currentLayout.value,
    preventMouseDownDefault: true, // Empêcher simple-keyboard de gérer le mousedown
    stopMouseDownPropagation: true,
    onKeyPress: (button: string) => {
      // Sauvegarder la position avant tout traitement
      onKeyboardInteractionStart();

      if (button === '{shift}' || button === '{lock}') {
        handleLayoutChange(currentLayout.value === 'default' ? 'shift' : 'default');
      } else if (button === '{symbols}') {
        handleLayoutChange('symbols');
      } else if (button === '{abc}') {
        handleLayoutChange('default');
      } else if (button === '{escape}') {
        hideKeyboard();
      } else {
        emit('keyPress', button);
      }
    },
    layout: {
      default: [
        '1 2 3 4 5 6 7 8 9 0',
        'a z e r t y u i o p',
        'q s d f g h j k l m',
        '{shift} w x c v b n . @ {bksp}',
        '{symbols} {space} {escape}',
      ],
      shift: [
        '1 2 3 4 5 6 7 8 9 0',
        'A Z E R T Y U I O P',
        'Q S D F G H J K L M',
        '{shift} W X C V B N _ - {bksp}',
        '{symbols} {space} {escape}',
      ],
      symbols: [
        'à â ä é è ê ë î ï ô ö ù û ü ç',
        'À Â Ä É È Ê Ë Î Ï Ô Ö Ù Û Ü Ç',
        '~ ! @ # $ % ^ & * ( ) _ +',
        '` - = { } [ ] | \\ : ; "',
        '{abc} < > , . ? / {bksp}',
      ],
    },
    display: {
      '{bksp}': '⌫',
      '{shift}': 'Maj',
      '{space}': 'Espace',
      '{escape}': 'Fermer ⨉',
      '{symbols}': '?123',
      '{abc}': 'ABC',
    },
    buttonTheme: [
      {
        class: "special-key-green",
        buttons: "{shift} {symbols} {abc} {bksp}"
      },
      {
        class: "special-key-grey",
        buttons: "{escape}"
      }
    ]
  });
});
</script>

<template>
  <div ref="keyboardContainer" class="simple-keyboard fixed bottom-0 left-0 right-0 z-[999999] bg-gray-200"></div>
</template>

<style>
.simple-keyboard .hg-button.special-key-grey {
  background-color: #a5a5a5;
}

.simple-keyboard .hg-button.special-key-green {
  background-color: var(--color-blue);
  color: white;
}

.simple-keyboard .hg-button.hg-function-key {
  flex-grow: 0.5;
}

.simple-keyboard .hg-button[data-skbtn="{space}"] {
  flex-grow: 3;
}
.hg-theme-default .hg-button.hg-standardBtn[data-skbtn="@"] {
  max-width: none;
}
.hg-theme-default .hg-button.hg-standardBtn[data-skbtn="@"] {
  max-width: none;
}
</style>



